import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { useTheme } from '../theme';
import { useWallet, formatAddress } from '../contexts/WalletContext';
import { fetchPrice, fetchQuote, checkAndApproveToken, executeSwap, getTxUrl, getTokenBalance } from '../swapApi';
import TokenIcon from './TokenIcon';
import { useIsMobile } from '../useMediaQuery';

const NATIVE_TOKEN = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

// Token lists per chain
const TOKENS_BY_CHAIN = {
  1: [
    { symbol: 'ETH',    address: NATIVE_TOKEN, decimals: 18 },
    { symbol: 'USDC',   address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
    { symbol: 'USDT',   address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
    { symbol: 'DAI',    address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18 },
    { symbol: 'WETH',   address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', decimals: 18 },
    { symbol: 'WBTC',   address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8 },
    { symbol: 'LINK',   address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', decimals: 18 },
    { symbol: 'UNI',    address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', decimals: 18 },
    { symbol: 'cbETH',  address: '0xBe9895146f7AF43049ca1c1AE358B0541Ea49704', decimals: 18 },
    { symbol: 'stETH',  address: '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84', decimals: 18 },
    { symbol: 'wstETH', address: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0', decimals: 18 },
    { symbol: 'rETH',   address: '0xae78736Cd615f374D3085123A210448E74Fc6393', decimals: 18 },
    { symbol: 'AAVE',   address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9', decimals: 18 },
    { symbol: 'CRV',    address: '0xD533a949740bb3306d119CC777fa900bA034cd52', decimals: 18 },
    { symbol: 'MKR',    address: '0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2', decimals: 18 },
    { symbol: 'PEPE',   address: '0x6982508145454Ce325dDbE47a25d4ec3d2311933', decimals: 18 },
  ],
  42161: [
    { symbol: 'ETH',      address: NATIVE_TOKEN, decimals: 18 },
    { symbol: 'USDC',     address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6 },
    { symbol: 'USDT',     address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', decimals: 6 },
    { symbol: 'DAI',      address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', decimals: 18 },
    { symbol: 'WETH',     address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', decimals: 18 },
    { symbol: 'ARB',      address: '0x912CE59144191C1204E64559FE8253a0e49E6548', decimals: 18 },
    { symbol: 'GMX',      address: '0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a', decimals: 18 },
    { symbol: 'WBTC',     address: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f', decimals: 8 },
    { symbol: 'USDC.e',   address: '0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8', decimals: 6 },
    { symbol: 'LINK',     address: '0xf97f4df75117a78c1A5a0DBb814Af92458539FB4', decimals: 18 },
    { symbol: 'UNI',      address: '0xFa7F8980b0f1E64A2062791cc3b0871572f1F7f0', decimals: 18 },
    { symbol: 'PENDLE',   address: '0x0c880f6761F1af8d9Aa9C466984b80DAb9a8c9e8', decimals: 18 },
  ],
  8453: [
    { symbol: 'ETH',    address: NATIVE_TOKEN, decimals: 18 },
    { symbol: 'USDC',   address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6 },
    { symbol: 'DAI',    address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', decimals: 18 },
    { symbol: 'WETH',   address: '0x4200000000000000000000000000000000000006', decimals: 18 },
    { symbol: 'CBBTC',  address: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf', decimals: 8 },
    { symbol: 'AERO',   address: '0x940181a94A35A4569E4529A3CDfB74e38FD98631', decimals: 18 },
    { symbol: 'BRETT',  address: '0x532f27101965dd16442E59d40670FaF5eBB142E4', decimals: 18 },
    { symbol: 'DEGEN',  address: '0x4ed4E862860beD51a9570b96d89aF5E1B0Efefed', decimals: 18 },
    { symbol: 'TOSHI',  address: '0xAC1Bd2486aAf3B5C0fc3Fd868558b082a531B2B4', decimals: 18 },
    { symbol: 'USDbC',  address: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA', decimals: 6 },
  ],
  10: [
    { symbol: 'ETH',    address: NATIVE_TOKEN, decimals: 18 },
    { symbol: 'USDC',   address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', decimals: 6 },
    { symbol: 'USDT',   address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', decimals: 6 },
    { symbol: 'DAI',    address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', decimals: 18 },
    { symbol: 'WETH',   address: '0x4200000000000000000000000000000000000006', decimals: 18 },
    { symbol: 'OP',     address: '0x4200000000000000000000000000000000000042', decimals: 18 },
    { symbol: 'VELO',   address: '0x9560e827aF36c94D2Ac33a39bCE1Fe78631088Db', decimals: 18 },
    { symbol: 'SNX',    address: '0x8700dAec35aF8Ff88c16BdF0418774CB3D7599B4', decimals: 18 },
    { symbol: 'AAVE',   address: '0x76FB31fb4af56892A25e32cFC43De717950c9278', decimals: 18 },
  ],
  137: [
    { symbol: 'MATIC',  address: NATIVE_TOKEN, decimals: 18 },
    { symbol: 'USDC',   address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6 },
    { symbol: 'USDT',   address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
    { symbol: 'DAI',    address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', decimals: 18 },
    { symbol: 'WETH',   address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619', decimals: 18 },
    { symbol: 'WBTC',   address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6', decimals: 8 },
    { symbol: 'WMATIC', address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', decimals: 18 },
    { symbol: 'AAVE',   address: '0xD6DF932A45C0f255f85145f286eA0b292B21C90B', decimals: 18 },
    { symbol: 'LINK',   address: '0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39', decimals: 18 },
    { symbol: 'CRV',    address: '0x172370d5Cd63279eFa6d502DAB29171933a610AF', decimals: 18 },
    { symbol: 'BAL',    address: '0x9a71012B13CA4d3D0Cdc72A177DF3ef03b0E76A3', decimals: 18 },
  ],
};

const CHAIN_NAMES = {
  1: 'Ethereum', 42161: 'Arbitrum', 8453: 'Base', 10: 'Optimism', 137: 'Polygon',
};

/* ── CSS keyframes injected once ── */
const STYLE_ID = 'swap-widget-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes sw-confetti-burst {
      0% { transform: translate(0,0) scale(1); opacity: 1; }
      100% { transform: translate(var(--sw-cx), var(--sw-cy)) scale(0); opacity: 0; }
    }
    @keyframes sw-checkmark-scale {
      0% { transform: scale(0) rotate(-30deg); opacity: 0; }
      50% { transform: scale(1.2) rotate(5deg); opacity: 1; }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }
    @keyframes sw-glow-border {
      0% { box-shadow: 0 0 4px rgba(13,122,78,0.2); border-color: rgba(13,122,78,0.35); }
      50% { box-shadow: 0 0 16px rgba(13,122,78,0.45); border-color: rgba(13,122,78,0.6); }
      100% { box-shadow: 0 0 4px rgba(13,122,78,0.2); border-color: rgba(13,122,78,0.35); }
    }
    @keyframes sw-glow-border-dark {
      0% { box-shadow: 0 0 4px rgba(46,160,67,0.15); border-color: rgba(46,160,67,0.35); }
      50% { box-shadow: 0 0 16px rgba(46,160,67,0.5); border-color: rgba(46,160,67,0.7); }
      100% { box-shadow: 0 0 4px rgba(46,160,67,0.15); border-color: rgba(46,160,67,0.35); }
    }
    @keyframes sw-bounce-dot {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-6px); }
    }
    @keyframes sw-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes sw-countdown-ring {
      from { stroke-dashoffset: 0; }
      to { stroke-dashoffset: 75.4; }
    }
    @keyframes sw-price-flash-green {
      0% { background-color: rgba(13,122,78,0.15); }
      100% { background-color: transparent; }
    }
    @keyframes sw-price-flash-red {
      0% { background-color: rgba(184,0,26,0.1); }
      100% { background-color: transparent; }
    }
    @keyframes sw-token-glow {
      0% { box-shadow: 0 0 0px rgba(13,122,78,0); }
      100% { box-shadow: 0 0 12px rgba(13,122,78,0.2); }
    }
  `;
  document.head.appendChild(style);
}

/* ── Confetti Burst component ── */
function ConfettiBurst({ active, onDone }) {
  const particles = useMemo(() => {
    if (!active) return [];
    return Array.from({ length: 20 }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / 20 + (Math.random() - 0.5) * 0.5;
      const dist = 60 + Math.random() * 80;
      const colors = ['#0D7A4E', '#2EA043', '#FFD700', '#E0006E', '#5470D4', '#E8471A', '#1A8ED4', '#6B35C8'];
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        color: colors[i % colors.length],
        size: 4 + Math.random() * 5,
        isCircle: Math.random() > 0.5,
        delay: Math.random() * 0.15,
      };
    });
  }, [active]);

  useEffect(() => {
    if (active) {
      const t = setTimeout(() => onDone && onDone(), 1200);
      return () => clearTimeout(t);
    }
  }, [active, onDone]);

  if (!active || particles.length === 0) return null;

  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: p.isCircle ? '50%' : '1px',
            backgroundColor: p.color,
            '--sw-cx': `${p.x}px`,
            '--sw-cy': `${p.y}px`,
            animation: `sw-confetti-burst 0.9s ${p.delay}s cubic-bezier(0.25,0.46,0.45,0.94) forwards`,
          }}
        />
      ))}
    </div>
  );
}

/* ── Success checkmark ── */
function SuccessCheckmark({ visible }) {
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 99,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'linear-gradient(135deg, #0D7A4E 0%, #2EA043 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'sw-checkmark-scale 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
        boxShadow: '0 4px 24px rgba(13,122,78,0.4)',
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <motion.path
            d="M5 12l5 5L19 7"
            stroke="#FFFFFF"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          />
        </svg>
      </div>
    </div>
  );
}

/* ── Bouncing dots loading indicator ── */
function BouncingDots({ color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, height: 14 }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: 5, height: 5, borderRadius: '50%',
            backgroundColor: color,
            display: 'inline-block',
            animation: `sw-bounce-dot 1.2s ${i * 0.15}s ease-in-out infinite`,
          }}
        />
      ))}
    </span>
  );
}

/* ── Quote countdown ring ── */
function QuoteCountdown({ active, duration = 15, colors, dark }) {
  const [remaining, setRemaining] = useState(duration);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setRemaining(duration);
      return;
    }
    startRef.current = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const left = Math.max(0, duration - elapsed);
      setRemaining(left);
      if (left > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, duration]);

  // Reset on re-activation
  useEffect(() => {
    if (active) {
      startRef.current = Date.now();
      setRemaining(duration);
    }
  }, [active, duration]);

  if (!active) return null;

  const radius = 12;
  const circumference = 2 * Math.PI * radius;
  const progress = remaining / duration;
  const offset = circumference * (1 - progress);
  const isLow = remaining < 5;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted,
    }}>
      <svg width="28" height="28" viewBox="0 0 28 28" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="14" cy="14" r={radius} fill="none"
          stroke={dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} strokeWidth="2" />
        <circle cx="14" cy="14" r={radius} fill="none"
          stroke={isLow ? '#B8001A' : colors.green} strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.3s linear, stroke 0.3s ease' }}
        />
      </svg>
      <span style={{ color: isLow ? '#B8001A' : colors.labelMuted, fontWeight: isLow ? 600 : 400 }}>
        {Math.ceil(remaining)}s
      </span>
    </div>
  );
}

/* ── Animated count-up display for the "You receive" amount ── */
function AnimatedAmount({ value, color, mutedColor, flashDirection }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = parseFloat(prevRef.current) || 0;
    const to = parseFloat(value) || 0;
    prevRef.current = value;

    if (from === to || to === 0) {
      setDisplay(value);
      return;
    }

    const duration = 600;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (to - from) * eased;
      setDisplay(current.toLocaleString(undefined, { maximumFractionDigits: 6 }));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]);

  const flashAnim = flashDirection === 'up'
    ? 'sw-price-flash-green 0.5s ease-out'
    : flashDirection === 'down'
      ? 'sw-price-flash-red 0.5s ease-out'
      : 'none';

  return (
    <motion.span
      key={value}
      initial={{ opacity: 0.6, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{
        fontFamily: "'DM Serif Display', serif", fontSize: 28,
        color: value ? color : mutedColor,
        borderRadius: 6, padding: '0 4px',
        animation: flashAnim,
      }}
    >
      ~{display}
    </motion.span>
  );
}

/* ── Sparkle / confetti micro-animation near savings text ── */
function Sparkles() {
  const particles = useMemo(() => (
    Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 28,
      y: (Math.random() - 0.5) * 18,
      delay: Math.random() * 0.5,
      size: 3 + Math.random() * 3,
    }))
  ), []);

  return (
    <span style={{ position: 'relative', display: 'inline-block', width: 0, height: 0 }}>
      {particles.map(p => (
        <motion.span
          key={p.id}
          initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 1, 1, 0],
            scale: [0, 1.2, 1, 0],
            x: p.x,
            y: p.y,
          }}
          transition={{
            duration: 1.2,
            delay: p.delay,
            repeat: Infinity,
            repeatDelay: 2.5,
          }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: 'currentColor',
            pointerEvents: 'none',
          }}
        />
      ))}
    </span>
  );
}

function TokenSelector({ tokens, selectedToken, onSelect, open, onToggle, colors, dark, label }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onToggle(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => onToggle(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 600,
          color: colors.textPrimary, background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          border: `1px solid ${colors.border}`, borderRadius: 20, padding: '6px 12px 6px 8px',
          cursor: 'pointer', transition: 'all 0.15s ease',
          minHeight: 44, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
        }}
      >
        <TokenIcon symbol={selectedToken.symbol} size={20} />
        {selectedToken.symbol}
        <span style={{ fontSize: 8, opacity: 0.5 }}>&#9660;</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
              background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12,
              padding: 6, minWidth: 180,
              boxShadow: dark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelMuted,
              letterSpacing: 1, padding: '6px 10px 4px', fontWeight: 600,
            }}>
              {label}
            </div>
            {tokens.map((t, idx) => (
              <motion.button
                key={t.address}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.15, delay: idx * 0.03 }}
                onClick={() => { onSelect(t); onToggle(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 500,
                  color: t.address === selectedToken.address ? colors.green : colors.textPrimary,
                  background: t.address === selectedToken.address
                    ? (dark ? 'rgba(46,160,67,0.12)' : 'rgba(13,122,78,0.08)')
                    : 'transparent',
                  border: 'none', borderRadius: 8, padding: '10px 10px', cursor: 'pointer',
                  minHeight: 44, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={e => {
                  if (t.address !== selectedToken.address) {
                    e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
                    e.currentTarget.style.boxShadow = dark
                      ? '0 0 10px rgba(46,160,67,0.15)'
                      : '0 0 10px rgba(13,122,78,0.1)';
                  }
                }}
                onMouseLeave={e => {
                  if (t.address !== selectedToken.address) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <TokenIcon symbol={t.symbol} size={20} />
                {t.symbol}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Spinner for the swapping button state ── */
function ButtonSpinner() {
  return (
    <span style={{
      display: 'inline-block', width: 16, height: 16, marginRight: 8,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: '#FFFFFF',
      borderRadius: '50%',
      animation: 'sw-spin 0.7s linear infinite',
      verticalAlign: 'middle',
    }} />
  );
}

export default function SwapWidget({ initialSellToken, initialBuyToken, chainId: externalChainId }) {
  const { dark, colors } = useTheme();
  const { account, chainId: walletChainId, provider, signer, isConnected, connect, switchChain } = useWallet();
  const isMobile = useIsMobile();

  const activeChainId = externalChainId || walletChainId || 1;
  const tokens = useMemo(() => TOKENS_BY_CHAIN[activeChainId] || TOKENS_BY_CHAIN[1], [activeChainId]);

  const getInitialToken = useCallback((sym, fallbackIdx) => {
    if (sym) {
      const found = tokens.find(t => t.symbol.toUpperCase() === sym.toUpperCase());
      if (found) return found;
    }
    return tokens[fallbackIdx] || tokens[0];
  }, [tokens]);

  const [sellToken, setSellToken] = useState(() => getInitialToken(initialSellToken, 0));
  const [buyToken, setBuyToken] = useState(() => getInitialToken(initialBuyToken, 1));
  const [sellAmount, setSellAmount] = useState('');
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [approving, setApproving] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [feeExpanded, setFeeExpanded] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [flipRotation, setFlipRotation] = useState(0);
  const debounceRef = useRef(null);
  const [sellBalance, setSellBalance] = useState(null);
  const [buyBalance, setBuyBalance] = useState(null);
  const [swapCount, setSwapCount] = useState(0);

  // Success celebration state
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);

  // Price flash direction state
  const [priceFlash, setPriceFlash] = useState(null); // 'up' | 'down' | null
  const prevBuyAmountRef = useRef(null);

  // Input focus state
  const [inputFocused, setInputFocused] = useState(false);

  // Quote countdown key to reset timer
  const [quoteKey, setQuoteKey] = useState(0);

  // Format balance: max 6 decimals, remove trailing zeros
  const formatBalance = (raw, decimals) => {
    try {
      const formatted = ethers.formatUnits(raw, decimals);
      const num = parseFloat(formatted);
      if (num === 0) return '0';
      const str = num.toFixed(6);
      return str.replace(/\.?0+$/, '');
    } catch {
      return null;
    }
  };

  // Fetch sell token balance
  useEffect(() => {
    setSellBalance(null);
    if (!isConnected || !provider || !account) return;
    let cancelled = false;
    getTokenBalance(sellToken.address, account, provider)
      .then(bal => { if (!cancelled) setSellBalance(bal); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sellToken, account, provider, isConnected, activeChainId, swapCount]);

  // Fetch buy token balance
  useEffect(() => {
    setBuyBalance(null);
    if (!isConnected || !provider || !account) return;
    let cancelled = false;
    getTokenBalance(buyToken.address, account, provider)
      .then(bal => { if (!cancelled) setBuyBalance(bal); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [buyToken, account, provider, isConnected, activeChainId, swapCount]);

  // Reset tokens when chain changes
  useEffect(() => {
    const newTokens = TOKENS_BY_CHAIN[activeChainId] || TOKENS_BY_CHAIN[1];
    setSellToken(newTokens[0]);
    setBuyToken(newTokens[1]);
    setSellAmount('');
    setPriceData(null);
    setPriceError(null);
  }, [activeChainId]);

  // Fetch price preview (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPriceData(null);
    setPriceError(null);

    if (!sellAmount || parseFloat(sellAmount) <= 0) return;

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setPriceError(null);
      try {
        let amountInWei;
        try {
          amountInWei = ethers.parseUnits(sellAmount, sellToken.decimals).toString();
        } catch (parseErr) {
          setPriceError(`Invalid amount: ${sellAmount}`);
          setLoading(false);
          return;
        }

        if (amountInWei === '0') {
          setPriceError('Amount too small');
          setLoading(false);
          return;
        }

        const data = await fetchPrice(sellToken.address, buyToken.address, amountInWei, activeChainId);
        setPriceData(data);
        setQuoteKey(k => k + 1); // Reset countdown
      } catch (err) {
        console.error('Price fetch error:', err);
        setPriceData(null);
        setPriceError(err.message || 'Failed to fetch price');
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [sellAmount, sellToken, buyToken, activeChainId]);

  const buyAmount = useMemo(() => {
    if (!priceData?.buyAmount) return null;
    try {
      return ethers.formatUnits(priceData.buyAmount, buyToken.decimals);
    } catch {
      return null;
    }
  }, [priceData, buyToken]);

  // Track price direction changes for flash effect
  useEffect(() => {
    if (buyAmount) {
      const current = parseFloat(buyAmount);
      const prev = prevBuyAmountRef.current;
      if (prev !== null && prev !== current) {
        setPriceFlash(current > prev ? 'up' : 'down');
        const t = setTimeout(() => setPriceFlash(null), 500);
        return () => clearTimeout(t);
      }
      prevBuyAmountRef.current = current;
    } else {
      prevBuyAmountRef.current = null;
    }
  }, [buyAmount]);

  const rate = useMemo(() => {
    if (!buyAmount || !sellAmount || parseFloat(sellAmount) === 0) return null;
    return (parseFloat(buyAmount) / parseFloat(sellAmount)).toFixed(4);
  }, [buyAmount, sellAmount]);

  const handleFlip = () => {
    setFlipRotation(prev => prev + 180);
    const tempToken = sellToken;
    const tempAmount = buyAmount;
    setSellToken(buyToken);
    setBuyToken(tempToken);
    setSellAmount(tempAmount ? parseFloat(tempAmount).toFixed(6) : '');
  };

  const triggerSuccessCelebration = () => {
    setShowConfetti(true);
    setShowCheckmark(true);
    setTimeout(() => setShowCheckmark(false), 2000);
  };

  const handleSwap = async () => {
    if (!isConnected) {
      try {
        await connect();
      } catch (err) {
        const msg = err.message || 'Failed to connect wallet';
        toast.error(msg, { duration: 4000 });
        return;
      }
      return;
    }

    if (walletChainId !== activeChainId) {
      try {
        await switchChain(activeChainId);
        toast.success(`Switched to ${CHAIN_NAMES[activeChainId]}`, { duration: 2000 });
      } catch {
        toast.error('Failed to switch network');
        return;
      }
    }

    if (!sellAmount || parseFloat(sellAmount) <= 0) return;

    setSwapping(true);
    try {
      const amountInWei = ethers.parseUnits(sellAmount, sellToken.decimals).toString();

      // Step 1: Check approval
      setApproving(true);
      const approveTx = await checkAndApproveToken(sellToken.address, amountInWei, signer);
      if (approveTx) {
        toast.loading('Approving token...', { id: 'approve' });
        await approveTx.wait();
        toast.dismiss('approve');
        toast.success('Token approved', { duration: 2000 });
      }
      setApproving(false);

      // Step 2: Get binding quote
      toast.loading('Getting best quote...', { id: 'quote' });
      const quote = await fetchQuote(sellToken.address, buyToken.address, amountInWei, account, activeChainId);
      toast.dismiss('quote');

      // Step 3: Execute swap
      toast.loading('Confirm in wallet...', { id: 'swap' });
      const tx = await executeSwap(quote, signer);
      toast.dismiss('swap');

      toast.loading('Swap pending...', { id: 'pending' });
      const receipt = await tx.wait();
      toast.dismiss('pending');

      // Trigger celebration
      triggerSuccessCelebration();

      const txUrl = getTxUrl(receipt.hash, activeChainId);
      toast.success(
        (t) => (
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Swap successful!</div>
            <a href={txUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: colors.green, textDecoration: 'underline', fontSize: 11 }}>
              View transaction &#8599;
            </a>
          </div>
        ),
        { duration: 8000 }
      );

      setSellAmount('');
      setPriceData(null);
      setSwapCount(c => c + 1);
    } catch (err) {
      toast.dismiss('approve');
      toast.dismiss('quote');
      toast.dismiss('swap');
      toast.dismiss('pending');

      const msg = err?.info?.error?.message || err.reason || err.message || 'Swap failed';
      toast.error(msg, { duration: 4000 });
    } finally {
      setSwapping(false);
      setApproving(false);
    }
  };

  const getButtonContent = () => {
    if (!isConnected) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle' }}>
            <rect x="2" y="6" width="20" height="14" rx="3" stroke="#FFFFFF" strokeWidth="2" fill="none" />
            <path d="M16 13a1 1 0 100-2 1 1 0 000 2z" fill="#FFFFFF" />
            <path d="M6 6V5a4 4 0 014-4h4a4 4 0 014 4v1" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>
          Connect Wallet
        </span>
      );
    }
    if (walletChainId !== activeChainId) return `Switch to ${CHAIN_NAMES[activeChainId]}`;
    if (!sellAmount || parseFloat(sellAmount) <= 0) return 'Enter amount';
    if (loading) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Fetching<BouncingDots color={colors.labelMuted} /></span>;
    if (approving) return <span style={{ display: 'inline-flex', alignItems: 'center' }}><ButtonSpinner />Approving...</span>;
    if (swapping) return <span style={{ display: 'inline-flex', alignItems: 'center' }}><ButtonSpinner />Swapping...</span>;
    return 'Swap';
  };

  const isButtonDisabled = () => {
    if (!isConnected) return false;
    if (walletChainId !== activeChainId) return false;
    if (!sellAmount || parseFloat(sellAmount) <= 0) return true;
    if (loading || swapping) return true;
    return false;
  };

  // Button visual state
  const buttonState = (() => {
    if (!isConnected) return 'connect';
    if (walletChainId !== activeChainId) return 'switch';
    if (!sellAmount || parseFloat(sellAmount) <= 0) return 'enter';
    if (loading) return 'loading';
    if (approving || swapping) return 'swapping';
    return 'swap';
  })();

  const getButtonBackground = () => {
    switch (buttonState) {
      case 'connect':
        return 'linear-gradient(135deg, #1A8ED4 0%, #5470D4 100%)';
      case 'switch':
        return 'linear-gradient(135deg, #E0006E 0%, #D40010 100%)';
      case 'enter':
      case 'loading':
        return dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
      case 'swapping':
        return `linear-gradient(135deg, ${colors.green} 0%, #0A5E3A 100%)`;
      case 'swap':
      default:
        return `linear-gradient(135deg, ${colors.green} 0%, #0A5E3A 100%)`;
    }
  };

  const getButtonColor = () => {
    switch (buttonState) {
      case 'connect':
      case 'switch':
      case 'swapping':
      case 'swap':
        return '#FFFFFF';
      case 'enter':
      case 'loading':
      default:
        return colors.labelMuted;
    }
  };

  const hasValidQuote = priceData && buyAmount && !loading;
  const estGasFee = priceData?.gas ? `~$${(parseFloat(priceData.gas) * 0.00000002 * 2500).toFixed(2)}` : null;

  // Gradient background for widget card
  const cardBackground = dark
    ? 'linear-gradient(168deg, #161B22 0%, #1A2233 45%, #161B22 100%)'
    : 'linear-gradient(168deg, #FFFFFF 0%, #F8FBF9 40%, #FAFCFF 100%)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'relative',
        background: cardBackground,
        borderRadius: 16,
        border: `1px solid ${hasValidQuote ? (dark ? 'rgba(46,160,67,0.35)' : 'rgba(13,122,78,0.2)') : colors.border}`,
        boxShadow: dark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.06)',
        padding: isMobile ? 20 : 24,
        width: isMobile ? '100%' : 420,
        maxWidth: '100%',
        overflow: 'hidden',
        transition: 'border-color 0.6s ease, box-shadow 0.6s ease',
      }}
    >
      {/* Confetti burst overlay */}
      <ConfettiBurst active={showConfetti} onDone={() => setShowConfetti(false)} />

      {/* Success checkmark overlay */}
      <SuccessCheckmark visible={showCheckmark} />

      {/* Pulsing border glow overlay when quote is ready */}
      <AnimatePresence>
        {hasValidQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', inset: -1, borderRadius: 17, pointerEvents: 'none',
              border: `1.5px solid ${dark ? 'rgba(46,160,67,0.3)' : 'rgba(13,122,78,0.18)'}`,
              boxShadow: `0 0 24px ${dark ? 'rgba(46,160,67,0.12)' : 'rgba(13,122,78,0.08)'}`,
            }}
          />
        )}
      </AnimatePresence>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20,
      }}>
        <span style={{
          fontFamily: "'DM Serif Display', serif", fontSize: 20, color: colors.textPrimary,
        }}>
          Swap
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Quote countdown timer */}
          <QuoteCountdown
            key={quoteKey}
            active={hasValidQuote}
            duration={15}
            colors={colors}
            dark={dark}
          />
          {isConnected && (
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted,
              background: colors.badgeBg, padding: '3px 8px', borderRadius: 12,
              border: `1px solid ${colors.border}`,
            }}>
              {formatAddress(account)}
            </span>
          )}
        </div>
      </div>

      {/* Sell input */}
      <div style={{
        background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: 12, padding: '16px 16px 14px',
        border: `1px solid ${inputFocused ? (dark ? 'rgba(46,160,67,0.5)' : 'rgba(13,122,78,0.4)') : colors.borderLight}`,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        animation: inputFocused ? (dark ? 'sw-glow-border-dark 2s ease-in-out infinite' : 'sw-glow-border 2s ease-in-out infinite') : 'none',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted,
            letterSpacing: 0.8, fontWeight: 600,
          }}>
            YOU PAY
          </span>
          <TokenSelector
            tokens={tokens.filter(t => t.address !== buyToken.address)}
            selectedToken={sellToken}
            onSelect={setSellToken}
            open={sellOpen}
            onToggle={setSellOpen}
            colors={colors}
            dark={dark}
            label="SELECT TOKEN"
          />
        </div>
        {isConnected && sellBalance !== null && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6,
            marginBottom: 6,
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.labelMuted,
            }}>
              Balance: {formatBalance(sellBalance, sellToken.decimals)}
            </span>
            <button
              onClick={() => {
                const formatted = formatBalance(sellBalance, sellToken.decimals);
                if (formatted) setSellAmount(formatted);
              }}
              style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
                color: colors.green, background: 'transparent', border: 'none',
                cursor: 'pointer', padding: '6px 8px', borderRadius: 4,
                minHeight: 32, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.1s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = dark ? 'rgba(13,122,78,0.15)' : 'rgba(13,122,78,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              MAX
            </button>
          </div>
        )}
        <input
          type="text"
          inputMode="decimal"
          placeholder="0.0"
          value={sellAmount}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          onChange={e => {
            const v = e.target.value;
            if (v === '' || /^\d*\.?\d*$/.test(v)) setSellAmount(v);
          }}
          style={{
            width: '100%', border: 'none', background: 'transparent', outline: 'none',
            fontFamily: "'DM Serif Display', serif", fontSize: 28, color: colors.textPrimary,
            padding: 0,
          }}
        />
      </div>

      {/* Flip button with rotation animation */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '-8px 0', position: 'relative', zIndex: 5 }}>
        <motion.button
          onClick={handleFlip}
          animate={{ rotate: flipRotation }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          style={{
            width: 44, height: 44, borderRadius: '50%',
            background: colors.surface, border: `2px solid ${colors.border}`,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: colors.textSecondary,
            transition: 'border-color 0.15s ease, color 0.15s ease',
            touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = colors.green; e.currentTarget.style.color = colors.green; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.textSecondary; }}
        >
          &#8645;
        </motion.button>
      </div>

      {/* Buy output */}
      <div style={{
        background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: 12, padding: '16px 16px 14px',
        border: `1px solid ${colors.borderLight}`,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted,
            letterSpacing: 0.8, fontWeight: 600,
          }}>
            YOU RECEIVE
          </span>
          <TokenSelector
            tokens={tokens.filter(t => t.address !== sellToken.address)}
            selectedToken={buyToken}
            onSelect={setBuyToken}
            open={buyOpen}
            onToggle={setBuyOpen}
            colors={colors}
            dark={dark}
            label="SELECT TOKEN"
          />
        </div>
        {isConnected && buyBalance !== null && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            marginBottom: 6,
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.labelMuted,
            }}>
              Balance: {formatBalance(buyBalance, buyToken.decimals)}
            </span>
          </div>
        )}
        <div style={{ minHeight: 34 }}>
          {loading ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontSize: 14, fontFamily: "'IBM Plex Mono', monospace", color: colors.labelMuted,
            }}>
              Fetching<BouncingDots color={colors.labelMuted} />
            </span>
          ) : buyAmount ? (
            <AnimatedAmount
              value={parseFloat(buyAmount).toLocaleString(undefined, { maximumFractionDigits: 6 })}
              color={colors.textPrimary}
              mutedColor={colors.labelMuted}
              flashDirection={priceFlash}
            />
          ) : (
            <span style={{
              fontFamily: "'DM Serif Display', serif", fontSize: 28, color: colors.labelMuted,
            }}>
              0.0
            </span>
          )}
        </div>
      </div>

      {/* Inline error message */}
      {priceError && !loading && (
        <div style={{
          marginTop: 8,
          padding: '8px 12px',
          borderRadius: 8,
          background: dark ? 'rgba(184,0,26,0.1)' : '#FFF0F2',
          border: `1px solid ${dark ? 'rgba(184,0,26,0.2)' : '#F0A0A8'}`,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: '#B8001A',
          lineHeight: 1.4,
          wordBreak: 'break-word',
        }}>
          {priceError}
        </div>
      )}

      {/* Fee breakdown */}
      {priceData && buyAmount && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          style={{ marginTop: 12 }}
        >
          <button
            onClick={() => setFeeExpanded(e => !e)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              width: '100%', padding: '10px 14px', borderRadius: 10,
              background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${colors.borderLight}`, cursor: 'pointer',
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary,
            }}
          >
            <span>1 {sellToken.symbol} = {rate} {buyToken.symbol}</span>
            <span style={{ fontSize: 8, opacity: 0.5, transform: feeExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              &#9660;
            </span>
          </button>

          <AnimatePresence>
            {feeExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{
                  padding: '12px 14px', marginTop: 4, borderRadius: 10,
                  background: dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                  border: `1px solid ${colors.borderLight}`,
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  {estGasFee && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                      <span style={{ color: colors.labelMuted }}>Network fee</span>
                      <span style={{ color: colors.textSecondary }}>{estGasFee}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                    <span style={{ color: colors.labelMuted }}>PoolScope fee</span>
                    <span style={{ color: colors.textSecondary }}>0.5%</span>
                  </div>
                  <div style={{ height: 1, background: colors.borderLight }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                    <span style={{ color: colors.labelMuted }}>MetaMask charges</span>
                    <span style={{ color: colors.labelMuted, textDecoration: 'line-through' }}>0.875%</span>
                  </div>
                  {/* Enhanced savings badge with gradient, checkmark animation, and sparkles */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                      color: colors.green, fontWeight: 600,
                      background: dark
                        ? 'linear-gradient(135deg, rgba(46,160,67,0.12) 0%, rgba(46,160,67,0.06) 100%)'
                        : 'linear-gradient(135deg, #E8F8F2 0%, #F0FFF7 100%)',
                      padding: '8px 12px', borderRadius: 8,
                      border: `1px solid ${colors.greenBorder}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Animated green checkmark */}
                    <motion.span
                      initial={{ scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 12, delay: 0.2 }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 18, height: 18, borderRadius: '50%',
                        background: dark
                          ? 'linear-gradient(135deg, #2EA043 0%, #1B7A35 100%)'
                          : 'linear-gradient(135deg, #0D7A4E 0%, #0A5E3A 100%)',
                        color: '#FFFFFF', fontSize: 10, fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      &#10003;
                    </motion.span>

                    <span>You save</span>

                    {/* Gradient badge for the percentage */}
                    <span style={{
                      display: 'inline-block',
                      background: dark
                        ? 'linear-gradient(135deg, #2EA043 0%, #3DCA5E 100%)'
                        : 'linear-gradient(135deg, #0D7A4E 0%, #14A06A 100%)',
                      color: '#FFFFFF',
                      padding: '2px 7px',
                      borderRadius: 4,
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: 0.3,
                    }}>
                      43%
                    </span>

                    <span>on fees vs MetaMask</span>

                    {/* Sparkle particles */}
                    <Sparkles />
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Swap button with distinct visual states */}
      <motion.button
        onClick={handleSwap}
        disabled={isButtonDisabled()}
        animate={
          buttonState === 'swap' && hasValidQuote
            ? {
                boxShadow: [
                  `0 0 0px ${dark ? 'rgba(46,160,67,0)' : 'rgba(13,122,78,0)'}`,
                  `0 0 24px ${dark ? 'rgba(46,160,67,0.4)' : 'rgba(13,122,78,0.25)'}`,
                  `0 0 0px ${dark ? 'rgba(46,160,67,0)' : 'rgba(13,122,78,0)'}`,
                ],
              }
            : buttonState === 'connect'
              ? {
                  boxShadow: [
                    '0 0 0px rgba(26,142,212,0)',
                    '0 0 16px rgba(26,142,212,0.25)',
                    '0 0 0px rgba(26,142,212,0)',
                  ],
                }
              : { boxShadow: '0 0 0px rgba(0,0,0,0)' }
        }
        transition={
          (buttonState === 'swap' && hasValidQuote) || buttonState === 'connect'
            ? { duration: 2, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.3 }
        }
        whileHover={!isButtonDisabled() ? { scale: 1.01, y: -1 } : {}}
        whileTap={!isButtonDisabled() ? { scale: 0.98 } : {}}
        style={{
          width: '100%', marginTop: 16, padding: '16px 0', borderRadius: 12,
          border: 'none', cursor: isButtonDisabled() ? 'not-allowed' : 'pointer',
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 600,
          letterSpacing: 0.3,
          background: getButtonBackground(),
          color: getButtonColor(),
          transition: 'background 0.3s ease, color 0.3s ease',
          opacity: (buttonState === 'swapping') ? 0.85 : 1,
          minHeight: 48,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {getButtonContent()}
      </motion.button>

      {/* Trust badges */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 12px',
        marginTop: 16, padding: '0 8px',
      }}>
        {['Non-custodial', 'No signup', '0.5% fee', 'Best routes'].map(badge => (
          <span key={badge} style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: colors.labelMuted, display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <span style={{ color: colors.green, fontSize: 11 }}>&#10003;</span>
            {badge}
          </span>
        ))}
      </div>

      {/* Powered by */}
      <div style={{
        textAlign: 'center', marginTop: 14,
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
        color: colors.labelLighter, letterSpacing: 0.5,
      }}>
        Powered by 0x Protocol
      </div>
    </motion.div>
  );
}
